<template>
    <dl class="form-row" :class="{'element-first': elementFirst, 'no-label': !label && !slots.label}">
        <dt class="form-label" v-if="label || slots.label">
            <label :for="labelFor" :class="{'intranet-headline-color': coloredLabel}" :aria-label="(label ? label : '') + (_required ? ' Pflichtfeld' :'')">
                <slot name="label">{{label}}</slot>
                <span class="required-marker" v-if="_required">*</span>
                <span class="screen-reader-only">{{helpText}}</span>
            </label>

            <span v-tooltip="{text: helpText, css: helpTooltipCss, align: helpAlignment}">
                <i v-if="help" class="material-icons form-label-help" aria-hidden="true" tabindex="0" role="img">info</i>
            </span>

            <div v-if="slots.description" class="padding-left-10">
                <slot name="description"/>
            </div>
        </dt>

        <dd class="form-element" :class="{invalid: isInvalid === true, valid: isInvalid === false, validate: errorName || hasValidators, 'popover-error': errorTooltip}">
            <div>
                <div v-bind="contentAttributes">
                    <slot :is-invalid="isInvalid"/>
                </div>

                <template v-if="isInvalid && !hideErrorMessage">
                    <app-popover v-if="errorTooltip" class="form-error-popover form-error" position="bottom-left" :focus-trap="false" arrow>
                        <template #default="{open, mouseleave}">
                            <span v-interact="open" aria-label="Fehler anzeigen" class="text-danger icon-interact md-20" @mouseleave="mouseleave" @mouseover="open" @focusin="open">
                                <i aria-hidden="true" class="material-icons" role="img">priority_high</i>
                            </span>
                        </template>

                        <template #container="{close}">
                            <form-error :id="formErrorId" :name="_errorName" @focusout="close"/>
                        </template>
                    </app-popover>
                    <form-error v-else :id="formErrorId" :name="_errorName"/>
                </template>
            </div>
            <span class="validate-status" v-if="_errorName && !hideValidator">
                <i v-if="hasValidators && isInvalid !== null && !hideValidatorIcon" :class="{'text-success': !isInvalid, 'text-danger': isInvalid}"
                    class="material-icons md-18"
                    v-text="isInvalid ? 'priority_high' : 'check'" aria-hidden="true" role="img"></i>
            </span>
        </dd>
    </dl>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {defineAsyncComponentWrapper} from '@/library/utils';
    import {getVueParent} from '@/library/dom';
    import FormError from './FormError.vue';
    import {
        computed,
        getCurrentInstance,
        useSlots,
        ref,
        nextTick,
        onMounted,
        watch,
        type ComponentPublicInstance,
    } from 'vue';
    import {useStore} from 'vuex';

    const AppPopover = defineAsyncComponentWrapper(() => import('../AppPopover.vue'));

    const portuna = usePortuna();

    type SwapErrorKey = {
        from: string;
        to: string;
    };
    const swapErrors        = ref<SwapErrorKey[]>([]);
    const swapTimeoutHandle = ref<number>(0);

    const props = withDefaults(
        defineProps<{
            label?: string;
            errorName?: string;
            hideErrorMessage?: boolean;
            hideValidator?: boolean;
            hideValidatorIcon?: boolean;
            filterName?: string;
            labelFor?: string;
            help?: string;
            helpAlignment?: string;
            required?: boolean;
            requiredManually?: boolean;
            elementFirst?: boolean;
            coloredLabel?: boolean;
            errorTooltip?: boolean;
            helpTooltipCss?: {
                [key: string]: string;
            };
            contentAttributes?: {
                [key: string]: string;
            };
            ignoreValidation?: boolean;
        }>(),
        {
            requiredManually: false,
            coloredLabel: false,
            errorTooltip: false,
            ignoreValidation: false,
            helpTooltipCss: () => ({}),
            contentAttributes: () => ({}),
        },
    );

    const slots = useSlots();

    const store = useStore();

    const defaultHelpText: { [key: string]: string } = {
        multipleFileUpload: 'Sie können bei diesem Datei-Upload beliebig viele Dateien in einem Schritt hochladen. Wählen Sie dazu einfach mehrere Dateien mit Strg + Mausklick in Ihrem Explorer aus.',
    };

    const isFieldsetContext = ref<boolean>(false);

    const formErrorId = computed(() =>
    {
        return 'form-error-' + portuna._uid;
    });

    const helpText = computed((): string =>
    {
        let help = props.help!;
        if ('undefined' !== typeof defaultHelpText[help])
        {
            help = defaultHelpText[help];
        }

        return help;
    });

    const _filterName = computed(() =>
    {
        return props.filterName || props.errorName || props.labelFor;
    });

    const _errorName = computed(() =>
    {
        return props.errorName || props.labelFor;
    });

    const isInvalid = computed(() =>
    {
        let errorName = _errorName.value;
        if (!errorName || props.ignoreValidation)
        {
            return null;
        }

        if (!store.state.form.submitted && !store.state.form.validated[errorName])
        {
            return null;
        }

        return typeof store.state.form.errors[errorName] !== 'undefined';
    });

    const filter = computed(() =>
    {
        return store.state.form.filter[_filterName.value!];
    });

    const hasValidators = computed(() =>
    {
        return typeof filter.value !== 'undefined' && filter.value.validators.length > 0;
    });

    const _required = computed(() =>
    {
        if (props.required)
        {
            return true;
        }
        else if (!props.requiredManually && hasValidators.value)
        {
            for (let validator of filter.value.validators)
            {
                if ('NotEmpty' === validator.name)
                {
                    return true;
                }
            }
        }

        return false;
    });

    onMounted(() =>
    {
        isFieldsetContext.value = !!getVueParent(getCurrentInstance()!.proxy as ComponentPublicInstance, 'FieldsetCloneable');
    });

    watch(_errorName, (newName: Optional<string>, oldName: Optional<string>) =>
    {
        // prevent empty swap due to possible duplication
        if (!isFieldsetContext.value || !newName)
        {
            return;
        }

        // add error-swap
        swapErrors.value.push({
            from: oldName!,
            to: newName,
        });

        // clear timeout in order to collect all errors pushed within 30ms
        if (swapTimeoutHandle.value)
        {
            window.clearTimeout(swapTimeoutHandle.value);
        }

        swapTimeoutHandle.value = window.setTimeout(() =>
        {
            let setErrors: {
                swap: SwapErrorKey;
                errors: Record<string, string>;
                validated: Record<string, boolean>;
            }[] = [];

            // collect current error-states
            for (const swap of swapErrors.value)
            {
                setErrors.push({
                    swap,
                    errors: store.state.form.errors[swap.from],
                    validated: store.state.form.validated[swap.from],
                });
            }

            // set errors to new keys
            for (const setError of setErrors)
            {
                store.dispatch('form/resetError', {errorName: setError.swap.from});

                nextTick(() =>
                {
                    store.dispatch('form/setErrorsAndValidated', {
                        errorName: setError.swap.to,
                        errors: setError.errors,
                        validated: setError.validated,
                    });
                });
            }

            swapErrors.value = [];
        }, 30);
    });

    defineExpose(Object.assign(
        {},
        portuna,
        {
            componentName: 'FormRow',
            formErrorId,
            _filterName,
            _errorName,
        },
    ));
</script>